import type { ButtonHTMLAttributes, AnchorHTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn';

/**
 * Button primitives.
 *
 * Variants:
 * - primary / secondary / ghost: pill-shaped, modern. Default in tool surfaces
 *   (@sica/web). Used for in-flow CTAs.
 * - institutional / institutionalOutline: rectangular, editorial. Default in
 *   the public site (@sica/site). Used for hero and high-authority CTAs.
 * - underline: text-only inline link with persistent underline, used as
 *   "Ver más →" affordance between sections.
 *
 * See docs/about/visual-identity.md §5.1 for the variant catalog and §11.2
 * for the per-package default.
 */
type Variant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'institutional'
  | 'institutionalOutline'
  | 'underline';

type Size = 'sm' | 'md' | 'lg' | 'xl';

const base =
  'focus-ring inline-flex items-center justify-center gap-2 font-medium transition-colors disabled:pointer-events-none disabled:opacity-50';

const variants: Record<Variant, string> = {
  primary: 'rounded-full bg-ink text-paper hover:bg-ink-muted',
  secondary:
    'rounded-full border border-line_strong text-ink hover:border-ink hover:bg-ink hover:text-paper',
  ghost: 'rounded-full text-ink hover:bg-paper-tinted',
  institutional:
    'rounded-none border border-accent bg-accent text-accent-contrast hover:bg-accent/90',
  institutionalOutline:
    'rounded-none border border-accent bg-transparent text-accent hover:bg-accent hover:text-accent-contrast',
  underline:
    'h-auto rounded-none p-0 text-accent underline decoration-1 underline-offset-[6px] hover:decoration-2',
};

const sizes: Record<Size, string> = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-5 py-2.5 text-sm',
  lg: 'px-7 py-3 text-base',
  xl: 'px-7 py-3.5 text-[0.95rem]',
};

const underlineSizeOverride: Record<Size, string> = {
  sm: 'text-sm',
  md: 'text-sm',
  lg: 'text-base',
  xl: 'text-[0.95rem]',
};

function classesFor(variant: Variant, size: Size, className?: string) {
  const sizeClass = variant === 'underline' ? underlineSizeOverride[size] : sizes[size];
  return cn(base, variants[variant], sizeClass, className);
}

export function Button({
  variant = 'primary',
  size = 'md',
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
}) {
  return (
    <button className={classesFor(variant, size, className)} {...props}>
      {children}
    </button>
  );
}

export function LinkButton({
  variant = 'primary',
  size = 'md',
  className,
  children,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
}) {
  return (
    <a className={classesFor(variant, size, className)} {...props}>
      {children}
    </a>
  );
}

export type ButtonVariant = Variant;
export type ButtonSize = Size;
